import { useEffect } from "react";

import { endOfMonth, format, startOfMonth } from "date-fns";
import { t } from "i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";
import ReportsServices from "@/services/ReportsService";

import { useFiltersContext } from "@/hooks/useFiltersContext";

import { FeatureGate } from "@/components/atoms";
import { PageLayout } from "@/components/templates";

import { projectKeys, reportKeys } from "@/constants/CQueryKeys";

import { TCompany, TProject } from "@/types";

import { ReportBody } from "./components/ReportBody";
import { ReportFiltersCard } from "./components/ReportFiltersCard";
import { ReportFinalizedHeader } from "./components/ReportFinalizedHeader";
import { ReportForm } from "./components/ReportForm";
import { ReportInvoicesCard } from "./components/ReportInvoicesCard";

export const ReportEditView = () => {
	const { id } = useParams<{ id?: string }>();
	const reportId = id ? parseInt(id, 10) : undefined;

	// A new report may be asked for a project
	// (`/reports/new?projectId=`, what the project menu sends); without
	// it the user picks in the form.
	const [searchParams] = useSearchParams();
	const askedProjectId = Number(searchParams.get("projectId")) || undefined;
	const { data: askedProject } = useQuery({
		queryKey: projectKeys.detail(askedProjectId ?? 0),
		queryFn: () => ProjectsService.getProject(askedProjectId!),
		enabled: !!askedProjectId,
	});

	const { filters, setFilter } = useFiltersContext("reports");

	// A new report starts on the current month. The period used to be
	// inherited from the tasks view, which is empty on a direct visit and,
	// when it is set, is the week being browsed - not what a report is
	// usually about.
	useEffect(() => {
		if (reportId) return; // editing: the saved report drives the filters

		if (!filters.dateFrom || !filters.dateTo) {
			const now = new Date();
			setFilter("dateFrom", format(startOfMonth(now), "yyyy-MM-dd"));
			setFilter("dateTo", format(endOfMonth(now), "yyyy-MM-dd"));
		}
	}, [reportId, filters.dateFrom, filters.dateTo, setFilter]);

	// The asked project, once the list has it.
	useEffect(() => {
		if (reportId) return;
		if (askedProject && filters.project?.id !== askedProject.id)
			setFilter("project", askedProject);
	}, [reportId, askedProject, filters.project?.id, setFilter]);

	const { data: existingReport, isLoading: isReportLoading } = useQuery({
		queryKey: reportKeys.savedDetail(reportId),
		queryFn: () => ReportsServices.get(reportId!),
		enabled: !!reportId,
	});

	// setFilter is recreated on every render by the lib's useFiltersContext,
	// and every call produces a new filters state even when nothing changed.
	// Listing it as a dependency therefore made this effect run on every
	// render and loop forever (render -> setFilter x4 -> provider update ->
	// render). Key on the report only.
	useEffect(() => {
		if (!existingReport) return;
		// A report covers a project or a client (docs/modele.md, phase 6).
		setFilter(
			"client",
			existingReport.client as unknown as TCompany | null
		);
		setFilter(
			"project",
			existingReport.project as unknown as TProject | undefined
		);
		setFilter("dateFrom", existingReport.dateFrom.slice(0, 10));
		setFilter("dateTo", existingReport.dateTo.slice(0, 10));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [existingReport]);

	return (
		<PageLayout
			title={t("reports.title")}
			isLoading={isReportLoading}
			onBack={() => window.history.back()}
		>
			<div className="mb-6 max-w-4xl">
				<ReportForm reportId={reportId} existingReport={existingReport}>
					{existingReport && !existingReport.isDraft ? (
						<ReportFinalizedHeader
							report={existingReport}
							className="mb-6"
						/>
					) : (
						<ReportFiltersCard className="mb-6" />
					)}
					<ReportBody />
				</ReportForm>
				{existingReport && (
					<FeatureGate feature="invoices">
						<ReportInvoicesCard
							report={existingReport}
							className="mt-6"
						/>
					</FeatureGate>
				)}
			</div>
		</PageLayout>
	);
};
